import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useMemo } from 'react';

import { BaseAuthedPage } from '../BaseAuthedPage';
import {
  hasOrganizationsModule,
  getAccountSettingsPath
} from '@rhino-project/core/utils';
import {
  Tabs,
  TabList,
  TabListContainer,
  Tab,
  TabIndicator,
  Typography
} from '@heroui/react';
import { Outlet, useLocation, createLink } from '@tanstack/react-router';
import { useBaseOwnerPath } from '../../hooks';

// Make the HeroUI Tab a TanStack-router link so it preloads on intent
// automatically (honoring the router's defaultPreload), the same way the app's
// nav links do via <Link>. createLink wires the router's hover/focus/touch
// preload handlers and click navigation onto the Tab.
const TabLink = createLink(Tab);

export const SettingsPage = () => {
  const { t } = useTranslation();
  const { build } = useBaseOwnerPath();
  const { pathname } = useLocation();

  const showSubscriptions = useMemo(() => !hasOrganizationsModule(), []);

  const settingsBuild = useCallback(
    (tabId: string) =>
      build(`${getAccountSettingsPath()}${tabId ? `/${tabId}` : ''}`),
    [build]
  );

  // A Tab's `id` is the React Aria collection key and MUST stay stable across
  // renders. The base owner resolves asynchronously on reload, so build() (and
  // any path derived from it) changes between renders -- keying a Tab on the
  // built path trips React Aria's "Cannot change the id of an item". Key on the
  // stable segment instead; the `to` prop still uses the built path.
  const tabs = useMemo(
    () => [
      {
        id: 'profile',
        label: t('rhino.frontend.messages.profile'),
        segment: ''
      },
      {
        id: 'sign-in',
        label: t('rhino.frontend.messages.sign_in_settings'),
        segment: 'sign-in'
      },
      {
        id: 'sessions',
        label: t('rhino.frontend.messages.sessions'),
        segment: 'sessions'
      },
      ...(showSubscriptions
        ? [
            {
              id: 'subscription',
              label: t('rhino.frontend.messages.subscription'),
              segment: 'subscription'
            }
          ]
        : [])
    ],
    [showSubscriptions, t]
  );

  const selectedKey = useMemo(() => {
    const match = tabs.find(
      (tab) => tab.segment && pathname.endsWith(`/${tab.segment}`)
    );
    return match ? match.id : 'profile';
  }, [pathname, tabs]);

  return (
    <BaseAuthedPage>
      <Typography.Heading level={3}>
        {t('rhino.frontend.messages.account_settings')}
      </Typography.Heading>
      <Tabs selectedKey={selectedKey}>
        <TabListContainer>
          <TabList aria-label={t('rhino.frontend.messages.account_settings')}>
            {tabs.map((tab) => (
              <TabLink key={tab.id} id={tab.id} to={settingsBuild(tab.segment)}>
                {tab.label}
                <TabIndicator />
              </TabLink>
            ))}
          </TabList>
        </TabListContainer>
      </Tabs>
      <div className="mt-4">
        <Outlet />
      </div>
    </BaseAuthedPage>
  );
};
